import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { ChevronRight, ChevronLeft, X, Home, BookOpen, ArrowRight, User, Calendar, Award, Target } from 'lucide-react';
import TableOfContents from './TableOfContents';
// ImageModal component
function ImageModal({ images, index, setIndex, close }) {
  const handlePrevious = () => {
    setIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : images.length - 1));
  };

  const handleNext = () => {
    setIndex((prevIndex) => (prevIndex < images.length - 1 ? prevIndex + 1 : 0));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Check if current item is a video
  const currentSrc = images[index];
  const isVideo = currentSrc.toLowerCase().endsWith('.mov') || 
                  currentSrc.toLowerCase().endsWith('.mp4') || 
                  currentSrc.toLowerCase().endsWith('.webm');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50" onClick={close}>
      <div className="relative max-w-4xl max-h-4xl w-full h-full flex items-center justify-center p-4">
        <button onClick={close} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10">
          <X className="w-8 h-8" />
        </button>

        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); handlePrevious(); }}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}

        {isVideo ? (
          <video
            src={currentSrc}
            controls
            autoPlay
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <img
            src={currentSrc}
            alt={`Image ${index + 1}`}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}

        {images.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        )}

        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black bg-opacity-50 px-3 py-1 rounded">
            {index + 1} / {images.length}
          </div>
        )}
      </div>
    </div>
  );
}

// SectionTitle component
function SectionTitle({ title }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-blue-600 to-indigo-700">
      <div className="max-w-4xl w-full text-center">
        <h1 className="text-6xl font-bold text-white mb-4">{title}</h1>
      </div>
    </div>
  );
}

// SectionHeader component
function SectionHeader({ title, description }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-4xl w-full text-center">
        <h1 className="text-5xl font-bold text-gray-800 mb-4">{title}</h1>
        <p className="text-xl text-gray-600">{description}</p>
      </div>
    </div>
  );
}

// DescriptionSlide component
function DescriptionSlide({ content }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-4xl w-full bg-white rounded-lg shadow-xl p-12">
        <div className="prose lg:prose-xl">
          <p className="text-gray-700 leading-relaxed">{content}</p>
        </div>
      </div>
    </div>
  );
}

// ProjectSlide component
function ProjectSlide({ project, isModalOpen, setIsModalOpen }) {  const [openModal, setOpenModal] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const { name, time, description, images, captions = [] } = project;

  const openModalAtIndex = (index) => {
  setModalIndex(index);
  setOpenModal(true);
  setIsModalOpen(true);
};

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-5xl w-full bg-white rounded-lg shadow-2xl p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold mb-2 text-gray-800">{name}</h1>
              {time && <h2 className="text-lg font-medium text-blue-600 mb-4">{time}</h2>}
            </div>
            
            {description && (
              <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500">
                <p className="text-gray-700 leading-relaxed">{description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg text-center">
                <Target className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="text-sm font-medium text-blue-800">Engineering Design</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg text-center">
                <Award className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <p className="text-sm font-medium text-green-800">Professional Quality</p>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-4 rounded-lg">
              <img
                src={images[0]}
                alt={`Project ${name} - View 1`}
                onClick={() => openModalAtIndex(0)}
                className="w-full h-auto object-contain cursor-pointer shadow-lg rounded-lg hover:shadow-xl transition-shadow duration-300"
                style={{ filter: 'contrast(1.1) brightness(0.95)', maxHeight: '400px' }}
              />

              {captions[0] && (
                <div className="absolute bottom-6 left-6 bg-black bg-opacity-70 text-white text-sm px-3 py-1 rounded">
                  {captions[0]}
                </div>
              )}

              {images.length > 1 && (
                <div className="absolute top-6 right-6 bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                  +{images.length - 1} more
                </div>
              )}
            </div>
          </div>
        </div>

        {openModal && (
          <ImageModal
            images={images}
            index={modalIndex}
            setIndex={setModalIndex}
            close={() => {
              setOpenModal(false);
              setTimeout(()=>{
                setIsModalOpen(false);  
              }, 100);
            }}
          />
        )}
      </div>
    </div>
  );
}

// 3D Animation Component
function EngineeringAnimation() {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, 1, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    
    const size = Math.min(mountRef.current.clientWidth, mountRef.current.clientHeight);
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    mountRef.current.appendChild(renderer.domElement);

    const gearGeometry = new THREE.CylinderGeometry(1, 1, 0.2, 8);
    const gearMaterial = new THREE.MeshPhongMaterial({ 
      color: 0x4A90E2,
      shininess: 100
    });
    const gear = new THREE.Mesh(gearGeometry, gearMaterial);
    scene.add(gear);

    for (let i = 0; i < 3; i++) {
      const smallGear = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3, 0.3, 0.1, 6),
        new THREE.MeshPhongMaterial({ color: 0x50C878 })
      );
      const angle = (i * Math.PI * 2) / 3;
      smallGear.position.set(Math.cos(angle) * 1.5, 0, Math.sin(angle) * 1.5);
      scene.add(smallGear);
    }

    const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    camera.position.set(0, 2, 4);
    camera.lookAt(0, 0, 0);
    
    sceneRef.current = { scene, camera, renderer, gear };

    const animate = () => {
      if (sceneRef.current) {
        sceneRef.current.gear.rotation.y += 0.01;
        sceneRef.current.renderer.render(sceneRef.current.scene, sceneRef.current.camera);
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="w-32 h-32" />;
}

// All projects data
const allProjects = [
  {
    type: "table-of-contents"
  },
  // PRE-UNIVERSITY SECTION
  {
    type: "section-header",
    title: "Pre-University Projects",
    description: "High school design projects"
  },
  {
    type: "section-title",
    title: "High School"
  },
  {
    type: "description",
    content: "For high school, I attended St. Joseph Secondary School. I developed foundational skills in circuit design through my computer engineering project, combining technical knowledge with practical applications."
  },
  {
    name: "Rotation Tracker Sensor Circuit",
    time: "February 2024 – May 2024",
    description: "For my high school computer engineering culminating project, I created an elaborate rotation tracker circuit that determined the direction of rotation. For the sensor circuit, I created PCB using Eagle CAD and optimized resources for cost efficiency.",
    images: ["/images/projects/temp/rotation_tracker_circuit.jpg",
      "/videos/rotation-circuit-demo.mp4"
    ],
    
    captions: ["PCB Design for Rotation Tracker Circuit",
      "Circuit Demo Video"
    ]
  },

  // UNIVERSITY SECTION
  {
    type: "section-header", 
    title: "University Projects",
    description: "Showcase of my engineering design work at the University of Waterloo"
  },
  {
    type: "section-title",
    title: "First Year"
  },
  {
    type: "description",
    content: "I'm currently a First Year Environmental Engineering Student at the University of Waterloo. During my first year, I completed various design projects focused on sustainable design principles and teamwork, applying engineering fundamentals to real-world problems through collaborative projects."
  },
  {
    name: "Waterloo University Residence Proposal",
    time: "September 2024 - December 2024",
    description: "Environmental engineering project where our team designed and proposed a sustainable residence to align with building codes, zoning regulations, and green design principles. We created a cost-effective community-oriented design prioritizing pedestrian flow, stormwater management, and energy efficiency using AutoCAD Civil 3D and Infraworks.",
    images: ["/images/projects/MHR-Project.png"],
    captions: ["MHR Proposal Showcase Poster"]
  },

  // CO-OP SECTION
  {
    type: "section-header",
    title: "Co-op Experience",
    description: "Design projects from Co-op terms"
  },
  {
    type: "section-title",
    title: "1st Co-op"
  },
  {
    type: "description",
    content: "I was a mechanical designer at ACE Manufacturing for my first internship. During these 4 months, I learned a lot about mechanical design and completed various projects showcased in this section. I strengthened my AutoCAD skills and gained hands-on experience with manufacutirng and power tools. Ultimately, the work environment stimulated my experience with project management and manufacturing coordination while working on diverse engineering challenges."
  },
  {
    name: "Customized Bearing Housing Design",
    time: "January 2025 – May 2025",
    description: "This was the first design I engineered during my first Co-op term. The project involved creating a complete bearing housing system with multiple components and detailed engineering specifications.",
    images: [
      "/images/projects/bearing-housing/BearingHousing.png",
      "/images/projects/bearing-housing/PlateBase.png",
      "/images/projects/bearing-housing/Plate.png",
      "/images/projects/bearing-housing/BearingBolts.png"
    ],
    captions: [
      "Bearing Housing View",
      "Plate Base View",
      "Bearing Plate Base View",
      "Bearing Bolts View"
    ]
  },
  {
    name: "Waste Water Treatment Schematic Updates",
    time: "January 2025 – May 2025",
    description: "As my first contribution for a large project, I updated and refined the waste water treatment schematic for a paint finishing project, ensuring compliance with environmental standards and operational efficiency.",
    images: ["/images/projects/2139/WasteWaterSchematic.jpg"],
    captions: ["Waste Water Treatment Schematic"]
  },
  {
    name: "Removable Handrails",
    time: "January 2025 – May 2025",
    description: "With guidance from amazing colleagues, I designed safety removable handrails for a platform. I learned many vital mechanical concepts that allow something to be 'removable' or 'adjustable'. We ultimately split the longer handrail into two components for enhanced safety.",
    images: [
      "/images/projects/2138/handrails/plan-view.png",
      "/images/projects/2138/handrails/parts-list.png",
      "/images/projects/2138/handrails/detailed-views1.png",
      "/images/projects/2138/handrails/detailed-views2.png",
      "/images/projects/2138/handrails/detailed-views3.png",
      "/images/projects/2138/handrails/detailed-views4.png"
    ],
    captions: [
      "Plan View",
      "Detailed Parts List",
      "Detailed Parts View 1",
      "Detailed Parts View 2",
      "Detailed Parts View 3",
      "Detailed Parts View 4"
    ]
  },
  {
    name: "Conveyor Design",
    time: "January 2025 – May 2025",
    description: "This was the first major contribution I made for a project. With guidance from prior sources, structural analysis calculations, and communication with suppliers and colleagues, I designed a conveyor system and coordinated the respective manufacturing process.",
    images: [
      "/images/projects/2142/plan-view.png",
      "/images/projects/2142/decline-view.png",
      "/images/projects/2142/incline-view.png",
      "/images/projects/2142/washer-cross-section.png",
      "/images/projects/2142/detailed1.png",
      "/images/projects/2142/detailed2.png",
      "/images/projects/2142/detailed3.png",
      "/images/projects/2142/detailed4.png"
    ],
    captions: [
      "Plan View",
      "Decline View",
      "Incline View",
      "Washer Cross Section",
      "Detailed View 1",
      "Detailed View 2",
      "Detailed View 3",
      "Detailed View 4"
    ]
  },
  {
    name: "Rectangle to Circle Layout",
    time: "January 2025 - May 2025",
    description: "I learned to draw one of the more complex ducts: rectangle to circle. This project formally introduced me to the AutoCAD 3D feature and taught me the equations used for drawing this layout. The part can be used in various applications throughout the construction industry.",
    images: [
      "/images/projects/misc/3dview.png",
      "/images/projects/misc/2dplan.png",
      "/images/projects/misc/2delevation.png",
      "/images/projects/misc/layout.png"
    ],
    captions: [
      "3D View of Rectangle to Circle Duct",
      "2D Plan View Layout",
      "2D Elevation Drawing",
      "Flat Pattern Layout for Manufacturing"
    ]
  },
  {
    name: "Platform Extensions",
    time: "January 2025 - May 2025",
    description: "I designed two different platform extensions (36\" and 48\") for an existing cooling tunnel using industrial panels. The project included detailed parts lists and manufacturing specifications.",
    images: [
      "/images/projects/3100/36inch.png",
      "/images/projects/3100/48inch.png",
      "/images/projects/3100/detailed1.png",
      "/images/projects/3100/detailed2.png",
      "/images/projects/3100/detailed3.png"
    ],
    captions: [
      "36 Inch Platform Extension Design",
      "48 Inch Platform Extension Design",
      "Detailed Parts List",
      "Detailed Parts List Continued",
      "Detailed Parts List Continued 2"
    ]
  },
  {
    name: "Mixing Tank Project Lead",
    time: "January 2025 - May 2025",
    description: "This was one of the largest tasks I was given, and I am forever grateful for the experience. I led a waste water treatment project designing and engineering a chemical mixing tank system. I guided the project from design through construction, learning vital lessons about engineering and project management.",
    images: [
      "/images/projects/3022/flowdiagram.png",
      "/images/projects/3022/generallayout.png",
      "/images/projects/3022/pumpframe.png"
    ],
    captions: [
      "Process Flow Diagram",
      "General System Layout",
      "Pump Frame Design"
    ]
  },
  {
    name: "Riser Design Replacement",
    time: "January 2025 - May 2025",
    description: "I designed a riser system for waste water treatment and paint finishing systems. Risers are similar to shower and sprinkler systems but for mechanical parts. I took the initiative to learn and design a complete riser system replacement.",
    images: ["/images/projects/2142/risers.png"],
    captions: ["Riser System Design"]
  },
  {
    name: "3D Model for Combustion Chamber",
    time: "January 2025 – May 2025",
    description: "I used AutoCAD 3D modeling to create a visual enhancement for a combustion chamber, optimizing manufacturing times and providing clear construction guidance for the shop team.",
    images: ["/images/projects/2142/3dmodel.png"],
    captions: ["3D Model View"]
  },
  {
    name: "Blow Off System Frame",
    time: "January 2025 – May 2025",
    description: "I designed and created a blow off system component for a paint finishing system. Using SolidWorks for reference, I designed the complete system in AutoCAD with detailed views and specifications.",
    images: [
      "/images/projects/2143/leftend.png",
      "/images/projects/2143/elevation.png",
      "/images/projects/2143/rightend.png",
      "/images/projects/2143/plan.png",
      "/images/projects/2143/enclosure.png",
      "/images/projects/2143/detailed.png"
    ],
    captions: [
      "Left End View",
      "Elevation View",
      "Right End View",
      "Plan View",
      "Sound Enclosure",
      "Detailed View Parts List"
    ]
  },
  {
    name: "Belt Conveyor Systems",
    time: "January 2025 – May 2025",
    description: "I designed 2 belt conveyor systems of different dimensions and functionalities by consolidating my knowledge of mechanical parts, consulting with mechanics, and referencing previous drawings. The 12 inches and 24 inches refer to the width of the chain.",
    images: [
      "/images/projects/3286/12inch.png",
      "/images/projects/3286/24inch.png"
    ],
    captions: [
      "12 Inch Belt Conveyor System",
      "24 Inch Belt Conveyor System"
    ]
  },
  {
    name: "Fan Housing Design",
    time: "January 2025 – May 2025",
    description: "In my last 2 days of interning, my colleagues and I each designed fan housings for 1-2 fans - one of my most cherished moments of my first internship. I created this design using AutoCAD and fan datasheet specifications.",
    images: ["/images/projects/2143/fanhousing.png"],
    captions: ["Fan Housing Design"]
  }
];

export default function JasonPortfolioSlides() {
  const [currentView, setCurrentView] = useState('intro');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
    // Don't handle arrow keys if modal is open
      if (isModalOpen) {
        return;
      }
    
      switch(e.key) {
        case 'ArrowLeft':
          prevSlide();
          break;
        case 'ArrowRight':
          nextSlide();
          break;
        case 'Escape':
          setCurrentView('intro');
          setCurrentSlide(0);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isModalOpen, currentSlide]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % allProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + allProjects.length) % allProjects.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Title slide
  if (currentView === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-4xl text-center">
          <div className="flex justify-center mb-8">
            <EngineeringAnimation />
          </div>
          <h1 className="text-6xl font-bold text-gray-800 mb-4">Jason's Design Portfolio</h1>
          <p className="text-2xl text-gray-600 mb-2">
            Design Projects & Engineering Work
          </p>
          <p className="text-lg text-gray-500 mb-8">
            University of Waterloo • Co-op Experience • 14 Featured Projects
          </p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setCurrentView('overview')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              <User className="w-5 h-5" />
              View Overview
            </button>
            <button
              onClick={() => {setCurrentSlide(0); setCurrentView('projects');}}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              <BookOpen className="w-5 h-5" />
              View Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Overview slide
  if (currentView === 'overview') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-5xl">
          <h1 className="text-5xl font-bold text-gray-800 mb-8 text-center">Portfolio Overview</h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <Calendar className="w-6 h-6 text-blue-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-800">Timeline</h3>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• <strong>Feb 2024 - May 2024:</strong> High School Computer Engineering Culminating Project</li>
                <li>• <strong>Sep 2024 - Dec 2024:</strong> Environmental Engineering Final Project</li>
                <li>• <strong>Jan 2025 - May 2025:</strong> First Co-op Term</li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                <Award className="w-6 h-6 text-green-600 mr-2" />
                <h3 className="text-xl font-semibold text-gray-800">Key Skills</h3>
              </div>
              <ul className="space-y-2 text-gray-700">
                <li>• AutoCAD & SolidWorks</li>
                <li>• PCB Design (Eagle CAD)</li>
                <li>• Project Management</li>
                <li>• Manufacturing Coordination</li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl mb-8">
            <div className="flex items-center mb-4">
              <Target className="w-6 h-6 text-purple-600 mr-2" />
              <h3 className="text-xl font-semibold text-gray-800">Project Categories</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">3</div>
                <div className="text-sm text-gray-600">Conveyor Systems</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">3</div>
                <div className="text-sm text-gray-600">Water Treatment</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">2</div>
                <div className="text-sm text-gray-600">Safety Systems</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">6</div>
                <div className="text-sm text-gray-600">Mechanical Design</div>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={() => setCurrentView('intro')}
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </button>
            <button
              onClick={() => {setCurrentSlide(0);
                setCurrentView('projects');}}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              View Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Projects view - render different slide types
  const renderCurrentSlide = () => {
    const slide = allProjects[currentSlide];
    if (slide.type === "table-of-contents") {
      return <TableOfContents goToSlide={goToSlide} />;
    }
    if (slide.type === "section-header") {
      return <SectionHeader title={slide.title} description={slide.description} />;
    }
    
    if (slide.type === "section-title") {
      return <SectionTitle title={slide.title} />;
    }
    
    if (slide.type === "description") {
      return <DescriptionSlide content={slide.content} />;
    }
    
    return <ProjectSlide project={slide} isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />;
  };

  return (
    <div className="relative">
      {/* Navigation Header */}
      <div className="fixed top-4 left-4 right-4 z-40 flex justify-between items-center bg-white bg-opacity-95 backdrop-blur-sm rounded-lg px-6 py-3 shadow-lg">
        <button
          onClick={() => setCurrentView('intro')}
          className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
        >
          <Home className="w-5 h-5" />
          Home
        </button>
        
        <div className="flex items-center gap-4">
          <span className="text-gray-600 font-medium">
            {currentSlide + 1} / {allProjects.length}
          </span>
          <button
            onClick={() => setCurrentView('overview')}
            className="flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors"
          >
            <BookOpen className="w-5 h-5" />
            Overview
          </button>
        </div>
      </div>

      {/* Current Slide Content */}
      {renderCurrentSlide()}

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="fixed left-4 top-1/2 transform -translate-y-1/2 z-30 bg-white bg-opacity-95 backdrop-blur-sm hover:bg-opacity-100 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg transition-all duration-200"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        onClick={nextSlide}
        className="fixed right-4 top-1/2 transform -translate-y-1/2 z-30 bg-white bg-opacity-95 backdrop-blur-sm hover:bg-opacity-100 text-gray-700 hover:text-blue-600 p-3 rounded-full shadow-lg transition-all duration-200"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Slide Indicators */}
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-30 flex gap-2 bg-white bg-opacity-90 backdrop-blur-sm p-3 rounded-full">
        {allProjects.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-200 ${
              index === currentSlide ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
}